<template>
    <div class="mt-5 mb-5">
        <data-table ref="events"
                    :api-route="backendApiRoute"
                    :api-view-set="table.apiViewSet"
                    :name="table.name"
                    :columns="table.columns"
                    :search="table.search"
                    :search-placeholder="table.searchPlaceholder"
                    :filters="table.filters ? table.filters : []"
                    :initial-filters="initialFilters"
                    :actions="table.actions"
                    :orderings="table.orderings"
                    :initial-orderings="table.initialOrderings"
                    :row-actions="table.rowActions"
                    :key="table.key"
                    :destroy-title="'Удаление мероприятия'"
                    :context-menu-wrapper-id="''">
        </data-table>
        <div class="clearfix p-5"></div>
    </div>
</template>

<script>
    import DataTable from "../../../components/Table/DataTable";
    import {mapGetters} from "vuex";
    import api from "../../../api";
    import actions, {Action, AvailableAction, ObjectAction} from "../../../components/Table/actions";
    import {BaseEvent, eventFactory, EventKind, EventKindLabel} from "./event";
    import {Request} from "@/pages/AppPages/Requests/request";
    import store from "@/store";
    import EventBus from "@/bus";
    import {download} from "@/components/Files/download";
    import {ACTIVITIES} from "@/store/auth/auth";

    export default {
        name: "CommonEventList",
        components: { DataTable },
        props: {
            prefix: { type: String, default: 'common' },
            animal: { type: Object, default: () => { return {} } },
            eventKinds: { type: Array, default: () => { return [] } },
            eventGroup: { type: String },  // CatchingEvent, ClinicalEvent, KeepingEvent, DisposalEvent - для ability
            showKindColumn: { type: Boolean, default: true },
        },
        data() {
            const name = this.prefix + '-events-table';
            let columns = [];

            if (this.showKindColumn) {
                columns.push({ name: 'info_object', 'title': 'Мероприятие',
                    cellComponentName: 'EventInfoCell',
                    cellComponentPath: 'src/pages/DataTableCells/',
                    alwaysVisible: true
                })

            } else {
                columns.push({ name: 'id', 'title': 'Номер',
                    to: {
                        idColumn: 'id',
                        routeName: 'eventDetail',
                        prependWithN: true
                    },
                    alwaysVisible: true
                })
            }

            if (!this.animal.id) {
                columns.push({ name: 'animal_object.description_object', 'title': 'Животное',
                    extraDataFrom: ['animal_object.species_name', 'animal_object.breed_name', 'attributes.isAggressive'],
                    extraRaw: true,
                    cellComponentName: 'AnimalAvatarInfoCell',
                    cellComponentPath: 'src/pages/DataTableCells/'})
            }

            columns.push(
                { name: 'municipality', 'title': 'Место'},
                { name: 'executor_name', 'title': 'Провел'}
            );

            columns.push(
                { name: 'address_short_text', 'title': 'Адрес',},
                { name: 'request_object', 'title': 'Заявка',
                    cellComponentName: 'RequestNumberStatusCell',
                    cellComponentPath: 'src/pages/DataTableCells/'},
                { name: 'executor_contractor_name', 'title': 'Организация', extraDataFrom: 'executor_contract_number'},
                { name: 'execution_date', 'title': 'Проведено',
                    cellComponentName: 'DateTimeCell',
                    cellComponentPath: 'src/pages/DataTableCells/'},
                { name: 'created_at', 'title': 'Создано',
                    cellComponentName: 'DateTimeCell',
                    cellComponentPath: 'src/pages/DataTableCells/'},
            );


            return {
                table: {
                    name: name,
                    apiViewSet: api.events.default,
                    key: name,
                    search: true,
                    searchPlaceholder: 'Поиск по номеру мероприятия, животного, чипа, бирки, заявки, контракта',
                    columns: columns,
                    orderings: [
                        'id',
                        { 'info_object': 'id' },
                        { 'animal_object.description_object': 'animal_id' },
                        'execution_date',
                        'created_at',
                        { 'kind_name': 'kind' },
                        { 'request_object.id': 'request__id' },
                    ],
                    actions: [],
                    rowActions: [],
                    filters: [
                        { title: 'Тип мероприятия', name: 'kind', source: api.animals.eventKinds.list() },
                        { title: 'Отловлено повторно',
                            name: 'is_repeated_catching',
                            type: 'boolean',
                            link: 'kind',
                            linkValue: EventKind.INITIAL,
                            visibleCallback: (appliedFilters) => {
                                for (const appliedFilter of appliedFilters) {
                                    if (appliedFilter.key === 'kind' && appliedFilter.value === EventKind.INITIAL) {
                                        return true
                                    }
                                }
                                return false
                            }
                        },
                        { title: 'Животное в данный момент на содержании',
                            name: 'is_kept',
                            type: 'boolean',
                            link: 'kind',
                            linkValue: EventKind.KEEPING,
                            visibleCallback: (appliedFilters) => {
                                for (const appliedFilter of appliedFilters) {
                                    if (appliedFilter.key === 'kind' && appliedFilter.value === EventKind.KEEPING) {
                                        return true
                                    }
                                }
                                return false
                            }
                        },
                        { title: 'Тип контракта',
                            name: 'contract_type',
                            source: `${api.contracts.nullableTypes.list()}`,
                        },
                        { title: 'Вид возврата владельцу',
                            name: 'return_to_owner_type',
                            source: `${api.events.ownerTypes.list()}`,
                            link: 'kind',
                            linkValue: EventKind.RETURN_TO_OWNER,
                            visibleCallback: (appliedFilters) => {
                                for (const appliedFilter of appliedFilters) {
                                    if (appliedFilter.key === 'kind' && appliedFilter.value === EventKind.RETURN_TO_OWNER) {
                                        return true
                                    }
                                }
                                return false
                            }
                        },
                        { title: 'Тип контракта принимающей стороны',
                            name: 'transfer_contract_type',
                            source: `${api.contracts.types.list()}`,
                            link: 'kind',
                            linkValue: EventKind.TRANSFER,
                            visibleCallback: (appliedFilters) => {
                                for (const appliedFilter of appliedFilters) {
                                    if (appliedFilter.key === 'kind' && appliedFilter.value === EventKind.TRANSFER) {
                                        return true
                                    }
                                }
                                return false
                            }
                        },
                        { title: 'Основание для эвтаназии',
                            name: 'euthanasia_reason',
                            source: `${api.events.euthanasiaReasons.list()}`,
                            link: 'kind',
                            linkValue: EventKind.EUTHANASIA,
                            visibleCallback: (appliedFilters) => {
                                for (const appliedFilter of appliedFilters) {
                                    if (appliedFilter.key === 'kind' && appliedFilter.value === EventKind.EUTHANASIA) {
                                        return true
                                    }
                                }
                                return false
                            }
                        },
                        { title: 'Вид животного', name: 'animal_species', source: api.species.default.list() },
                        { title: 'Пол животного', name: 'animal_gender', source: api.animals.genders.list() },
                        { title: 'Дата проведения', name: 'execution_date', type: 'dateFromTo' }
                    ],
                    initialFilters: {},
                    initialOrderings: { info_object: 2 }
                },
                objectsToDelete: null
            }
        },
        computed: {
            ...mapGetters(['ability', 'userOrganizationId', 'isInController', 'userOrganizationActivities', 'isInContractor',
                'hasMunicipalityRestrictions', 'userOrganizationMunicipalities', 'userLastValidContract']),
            backendApiRoute() {
                return api.events.default.list()
            },
            initialFilters() {
                let filters = {};
                if (this.animal.id) {
                    filters['animal'] = this.animal.id
                }
                if (this.eventKinds.length > 0) {
                    filters['kind'] = this.eventKinds
                }
                return filters
            },
            initialFiltersForMunicipalityFilter() {
                let filters = [];
                if (this.hasMunicipalityRestrictions) {
                    for (const m of this.userOrganizationMunicipalities) {
                        filters.push({'key': 'municipality', 'value': m})
                    }
                }
                return filters
            }
        },
        mounted() {
            const vm = this;

            if (vm.initialFiltersForMunicipalityFilter.length !== 1) {
                vm.table.filters.unshift({
                    title: 'Муниципалитет',
                    name: 'municipality',
                    source: api.municipalities.default.list(),
                    labelKey: 'short_name',
                    hasSearch: true,
                    initialFilters: vm.initialFiltersForMunicipalityFilter
                })
            }

            // PETS-286: откатили изменения PETS-275
            // if (vm.isInController) {
                vm.table.filters.push({ title: 'Исполнитель',
                    name: 'executor',
                    source: `${api.events.contractors.list()}`,
                })
            // }
            if (vm.isInController) {
                vm.table.filters.push({ title: 'Заказчик',
                    name: 'customer',
                    source: `${api.events.customers.list()}`,
                })
            }

            if (vm.isSuperuser || vm.isInController && !vm.hasMunicipalityRestrictions && vm.userOrganizationActivities.includes(ACTIVITIES.CONTROL)) {
                vm.table.filters.push({
                    title: 'Животное агрессивно',
                    name: 'is_aggressive',
                    type: 'boolean'
                })
            }

            const canCreate = vm.ability.can('create', vm.eventGroup) && vm.eventGroup !== 'CatchingEvent';

            if (canCreate && vm.userLastValidContract) {
                let createAction = null;
                if (vm.eventKinds.length === 1) {
                    createAction = actions.create(() => {
                            vm.$router.push({
                                name: 'eventCreate',
                                params: {kind: vm.eventKinds[0], animalId: vm.animal.id}
                            })
                        }, canCreate
                    )
                } else {
                    let dropdownList = [];

                    for (let i = 0; i < vm.eventKinds.length; i++) {
                        const kind = vm.eventKinds[i];
                        const text = EventKindLabel[kind][0];
                        const route = { name: 'eventCreate', params: { animalId: vm.animal.id, kind: kind } };
                        dropdownList.push({text: text, route: route })
                    }

                    createAction = new Action({
                        name: 'createWithContext',
                        icon: 'plus',
                        class: 'btn-primary',
                        title: 'Добавить',
                        showTitle: false,
                        dropdownList: dropdownList,
                        emittedEvent: 'on-create',
                        available: canCreate
                    }, (choice) => {
                        vm.$router.push(choice.route)
                    });
                }
                vm.table.actions.push(createAction);
            }

            const isVetControl = vm.isInContractor && vm.userOrganizationActivities.includes(ACTIVITIES.CONTROL);
            if ((vm.isInController || isVetControl) && vm.prefix === 'common') {
                vm.table.actions.push(
                    actions.downloadXlsx(
                        (e, emittedEventNameStart, emittedEventNameFinish) => {
                            const downloadBaseUrl = api.events.xlsx.list();
                            const downloadUrl = downloadBaseUrl + vm.$refs.events.getQueryString();
                            EventBus.$emit(emittedEventNameStart)
                            download(downloadUrl).then((response) => {
                                vm.$bvToast.toast('Выгрузка завершена', {
                                    title: 'Выгрузка документа',
                                    autoHideDelay: 5000,
                                    variant: 'success'
                                })
                            })
                                .catch(() => {
                                    vm.$bvToast.toast('Не удалось произвести выгрузку', {
                                        title: 'Выгрузка документа',
                                        autoHideDelay: 5000,
                                        variant: 'danger'
                                    })
                                })
                                .finally(() => {
                                    EventBus.$emit(emittedEventNameFinish)
                                })
                        }, true, 'Выгрузить журнал мероприятий')
                );
            }

            if (vm.ability.can('destroy', vm.eventGroup)) {
                const destroyAction = new AvailableAction({ title: 'Удалить', icon: 'trash', emittedEvent: 'on-delete' }, (id, row) => {
                    const objectClass = eventFactory(row['kind']);
                    const object = new objectClass();
                    object.applyData(row);
                    return object
                }, (row) => {
                    const objectClass = new eventFactory(row['kind']);
                    const object = new objectClass();
                    object.applyData(row);
                    return store.getters.ability.can('destroy', object);
                })
                vm.table.rowActions.push(destroyAction);
            }
        }
    }
</script>

<style scoped>

</style>